/**
 * A labelled row of figures, with no chrome around it.
 *
 * NOT .hd-tile, which is a surface plus a border — a card. Phase 9 will decide
 * what these look like; until then they follow the rhythm the Landing feature
 * blocks already use, where content sits on the page background directly.
 *
 * The three builders below exist so that the nine figures appear in the SAME
 * ORDER on My Stats, a public profile and a tournament summary. They read from
 * three different payload shapes — a season row, a career aggregate and one
 * night's row — which is exactly the sort of near-miss that ends with 140+ and
 * 100+ swapped on one page out of three.
 */

import { formatAvg } from '../../lib/format.js';

export default function StatGrid({ stats }) {
  return (
    <div className="hd-stat-grid">
      {stats.map(({ label, value }) => (
        <div key={label}>
          <div className="hd-stat-label">{label}</div>
          <div className="hd-stat-value">{value}</div>
        </div>
      ))}
    </div>
  );
}

/** A season row, from /players/:id/stats or /public-profile. */
export function seasonStatList(season) {
  return [
    { label: 'Average',     value: formatAvg(season.threeDartAvg) },
    { label: 'First 9',     value: formatAvg(season.firstNineAvg) },
    { label: '100+',        value: season.total100Plus },
    { label: '140+',        value: season.total140Plus },
    { label: '180s',        value: season.total180s },
    { label: 'High out',    value: season.highestCheckout },
    { label: 'Legs won',    value: season.legsWon },
    { label: 'Legs lost',   value: season.legsLost },
    { label: 'Breaks',      value: season.totalBreaks },
  ];
}

/**
 * Career totals. Same nine, plus the two figures that only mean anything across
 * more than one night.
 */
export function allTimeStatList(allTime) {
  return [
    ...seasonStatList(allTime),
    { label: 'Wins',        value: allTime.wins },
    { label: 'Runner-ups',  value: allTime.runnerUps },
  ];
}

/**
 * One night's row, from /tournaments/:id/player-stats.
 *
 * The per-night payload names its counts differently from the season one —
 * count180s against total180s, breaksOfThrow against totalBreaks — because one
 * is a tally of a night and the other a sum of seasons. Points is last, and is
 * here rather than in the season list because a season's points are already in
 * its summary line.
 */
export function nightStatList(player) {
  return [
    { label: 'Average',   value: formatAvg(player.threeDartAvg) },
    { label: 'First 9',   value: formatAvg(player.firstNineAvg) },
    { label: '100+',      value: player.count100Plus },
    { label: '140+',      value: player.count140Plus },
    { label: '180s',      value: player.count180s },
    { label: 'High out',  value: player.highestCheckout },
    { label: 'Legs won',  value: player.legsWon },
    { label: 'Legs lost', value: player.legsLost },
    { label: 'Breaks',    value: player.breaksOfThrow },
    { label: 'Points',    value: player.placementPoints },
  ];
}
